import { Box, useColorMode } from '@chakra-ui/react'
import * as Frigade from '@frigade/react'
import { PropsWithChildren, useMemo } from 'react'

import { colors } from '@common/theme/colors'
import { FrigadeAiFeaturesFlow } from '~/components/FrigadeAiFeaturesFlow'
import { useAdminContext } from '~/components/hooks/useAdminContext'
import { useCurrentUser } from '~/components/hooks/useCurrentUser'
import { useFirm } from '~/components/hooks/useFirm'
import { isDev, isProd } from '~/util/env'
import { avatarSidToUrl } from '~/util/s3'

/**
 * Scopes Engage theme CSS variables to this subtree so they do not overwrite
 * `:root` tokens shared with Frigade Assistant guide cards (`#frigade-root`).
 */
export const FRIGADE_ENGAGE_THEME_SELECTOR = '[data-frigade-engage-theme]'

/**
 * Maps Rentana color tokens into Frigade’s theme shape.
 *
 * Frigade’s numbered neutral scale treats lower values as darker (unlike Chakra),
 * so light mode inverts gray[50]–gray[900]. Title/body text use `foreground` and
 * `neutral.400` (subtitles); keep those high-contrast against `background`/`surface`.
 */
export function getFrigadeTheme(isDark: boolean) {
  const foreground = isDark ? colors.gray[100] : colors.gray[900]
  const mutedForeground = isDark ? colors.gray[200] : colors.gray[700]

  return {
    colors: {
      primary: {
        foreground: colors.white,
        background: isDark ? colors.brand[400] : colors.brand[500],
        surface: isDark ? colors.brand[400] : colors.brand[500],
        border: isDark ? colors.brand[400] : colors.brand[500],
        hover: {
          background: isDark ? colors.brand[300] : colors.brand[600],
          surface: isDark ? colors.brand[300] : colors.brand[600],
          border: isDark ? colors.brand[300] : colors.brand[600],
        },
      },
      secondary: {
        foreground,
        background: isDark ? colors.gray[800] : colors.white,
        surface: isDark ? colors.gray[700] : colors.gray[50],
        border: isDark ? colors.gray[700] : colors.gray[200],
        hover: {
          background: isDark ? colors.gray[700] : colors.gray[50],
          surface: isDark ? colors.gray[600] : colors.gray[200],
          border: isDark ? colors.gray[600] : colors.gray[300],
        },
      },
      neutral: {
        foreground,
        background: isDark ? colors.gray[800] : colors.white,
        surface: isDark ? colors.gray[700] : colors.gray[50],
        border: isDark ? colors.gray[700] : colors.gray[200],
        // Frigade scale: 100 = darkest text-adjacent, 900 = lightest surface-adjacent.
        '100': isDark ? colors.gray[50] : colors.gray[900],
        '200': isDark ? foreground : colors.gray[800],
        '300': mutedForeground,
        // Subtitles / secondary body copy (`--fr-colors-neutral-400`).
        '400': isDark ? colors.gray[300] : mutedForeground,
        '500': isDark ? colors.gray[400] : colors.gray[500],
        '600': isDark ? colors.gray[500] : colors.gray[400],
        '700': isDark ? colors.gray[600] : colors.gray[300],
        '800': isDark ? colors.gray[700] : colors.gray[200],
        '900': isDark ? colors.gray[800] : colors.gray[50],
      },
    },
    fontFamilies: {
      default: 'Inter Variable, -apple-system, system-ui, sans-serif',
    },
    radii: {
      md: '6px',
      lg: '12px',
    },
  }
}

function FrigadePromotionsCollection() {
  return (
    <Box position="fixed" bottom="0" p="4" zIndex="toast">
      <Frigade.Collection collectionId="collection_Bt75xGNg" />
    </Box>
  )
}

export function FrigadeEngageWrapper({ children }: PropsWithChildren) {
  const user = useCurrentUser()
  const firm = useFirm()
  const { isImpersonating } = useAdminContext()
  const isSystemAdmin = user.admin
  const { colorMode } = useColorMode()

  const frigadeApiKey = process.env.NEXT_PUBLIC_FRIGADE_ENGAGE_API_KEY

  const shouldUseFrigadeEngage =
    frigadeApiKey && (isDev || (isProd && !isImpersonating && !isSystemAdmin))

  const frigadeTheme = useMemo(() => getFrigadeTheme(colorMode === 'dark'), [colorMode])

  if (!shouldUseFrigadeEngage) {
    return <>{children}</>
  }

  const [firstName, ...lastNameParts] = user.fullName.split(' ')
  const lastName = lastNameParts.join(' ')
  const imageUrl = user.avatarSid ? avatarSidToUrl(user.avatarSid) : undefined
  const userProperties = {
    name: user.fullName,
    firstName: firstName ?? user.fullName,
    lastName: lastName ?? '',
    email: user.email,
    imageUrl,
    role: user.role,
    timeCreated: user.timeCreated,
  }

  const groupProperties = {
    name: firm.name,
    timeCreated: firm.timeCreated,
    isDemo: firm.isDemo,
  }

  return (
    <Frigade.Provider
      apiKey={frigadeApiKey}
      userId={user.sid}
      userProperties={userProperties}
      groupId={firm.sid}
      groupProperties={groupProperties}
      theme={frigadeTheme}
      themeSelector={FRIGADE_ENGAGE_THEME_SELECTOR}
    >
      <Box data-frigade-engage-theme="" display="contents">
        {children}
        <FrigadeAiFeaturesFlow />
        <FrigadePromotionsCollection />
      </Box>
    </Frigade.Provider>
  )
}
